// ============================================================================
// apps/web/src/lib/use-portage-count.ts
// ----------------------------------------------------------------------------
// Compteur de demandes de portage « nouveau » (non traitées) — alimente le
// badge de l'entrée « Portage repas » (sidebar + tiroir + tuile accueil),
// comme les badges Newsletter / Candidatures / To-Do.
//
// La table portage_requests est en RLS sans policy (accès réservé au Worker) →
// on lit le compteur via le Worker (`GET /api/portage/count`, admin.all), pas
// en direct depuis le navigateur. Réservé admin.
//
// Rafraîchi : au montage, au focus / retour d'onglet, toutes les 60 s, et à la
// demande via `notifyPortageChanged()` (appelé par PortagePage au changement de
// statut).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './auth-context';
import { Capability } from '@magic/shared';
import { fetchPortageCount } from './worker';

const EVENT = 'magic:portage-changed';

// À appeler après tout changement de statut pour rafraîchir le badge aussitôt.
export function notifyPortageChanged(): void {
  window.dispatchEvent(new Event(EVENT));
}

export function useNewPortageCount(): number {
  const { hasCapability } = useAuth();
  const canSee = hasCapability(Capability.ADMIN_ALL);
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!canSee) { setCount(0); return; }
    try {
      const res = await fetchPortageCount();
      setCount(res.nouveaux ?? 0);
    } catch { /* silencieux : le badge n'est pas critique */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) { setCount(0); return; }
    void refresh();
    const onFocus = () => { void refresh(); };
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(); };
    const onEvt = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(EVENT, onEvt);
    const id = window.setInterval(() => { void refresh(); }, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(EVENT, onEvt);
      window.clearInterval(id);
    };
  }, [canSee, refresh]);

  return count;
}
